import { defaultRangeExtractor, type Range } from "@tanstack/react-virtual";
import type {
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "./Board.types";
import { getCellEntries, getCellKey, type BoardCells } from "./boardCells";
import { getAllPlacements } from "./boardCollapse";

/**
 * Without lanes, a column with more cards than this mounts only what's near
 * the viewport. With lanes, a board with more cards in total windows its
 * lanes and every cell. Smaller ones stay plain DOM.
 */
export const BOARD_WINDOWING_THRESHOLD = 50;

const countBoardCards = <TItem>(cells: BoardCells<TItem>) =>
    [...cells.columnCounts.values()].reduce((sum, count) => sum + count, 0);

/**
 * Whether a cell mounts only the cards near the viewport: a long column,
 * or any cell of a board with lanes and many cards in total.
 */
export const isCellWindowed = <TItem>(
    cells: BoardCells<TItem>,
    placement: BoardPlacement,
) =>
    (placement.laneId === null
        ? getCellEntries(cells, placement).length
        : countBoardCards(cells)) > BOARD_WINDOWING_THRESHOLD;

interface WindowedCellKeysArgs<TItem> {
    cells: BoardCells<TItem>;
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
}

/** Keys of the cells that window, sorted. */
export const getWindowedCellKeys = <TItem>({
    cells,
    columns,
    lanes,
}: WindowedCellKeysArgs<TItem>) =>
    getAllPlacements(columns, lanes)
        .filter((placement) => isCellWindowed(cells, placement))
        .map(getCellKey)
        .sort();

// Mirrors `--board-card-gap` in Board.module.scss.
export const BOARD_CARD_GAP = 8;

// Overscan covers drag auto-scroll, which moves up to ~40px a frame.
export const BOARD_CARD_OVERSCAN = 8;
export const BOARD_LANE_OVERSCAN = 0;

const ESTIMATED_CARD_HEIGHT = 112;
// List padding plus a footer create button.
const ESTIMATED_CELL_CHROME = 60;
const ESTIMATED_LANE_HEADER = 40;

export const estimateCardHeight = () => ESTIMATED_CARD_HEIGHT;

/** A lane block: its header over its tallest expanded cell. */
export const estimateLaneHeight = (cellCounts: readonly number[]) =>
    ESTIMATED_LANE_HEADER +
    (cellCounts.length === 0
        ? 0
        : Math.max(...cellCounts) * (ESTIMATED_CARD_HEIGHT + BOARD_CARD_GAP) +
          ESTIMATED_CELL_CHROME);

// Every card mounting or unmounting re-renders all of dnd-kit's sortables, so
// card windows move in chunks rather than one card at a time.
export const BOARD_CARD_CHUNK = 8;

const snapToChunks = (range: Range, chunk: number): Range => ({
    ...range,
    overscan: 0,
    startIndex: Math.max(
        Math.floor((range.startIndex - range.overscan) / chunk) * chunk,
        0,
    ),
    endIndex: Math.min(
        Math.ceil((range.endIndex + range.overscan + 1) / chunk) * chunk - 1,
        range.count - 1,
    ),
});

interface PinnedRangeExtractorArgs {
    pinnedIndexes: readonly number[];
    /** Window edges snap to multiples of this. */
    chunk: number;
}

/**
 * The visible range plus overscan, widened to whole chunks, and always
 * `pinnedIndexes` (the dragged or focused card).
 */
export const createPinnedRangeExtractor =
    ({ pinnedIndexes, chunk }: PinnedRangeExtractorArgs) =>
    (range: Range) => {
        const indexes = new Set(
            defaultRangeExtractor(snapToChunks(range, chunk)),
        );
        pinnedIndexes.forEach((index) => {
            if (index < range.count) {
                indexes.add(index);
            }
        });
        return [...indexes].sort((first, second) => first - second);
    };

interface BoardWindowItem {
    kind: "item";
    index: number;
}

interface BoardWindowSpacer {
    kind: "spacer";
    key: string;
    size: number;
}

export type BoardWindowRun = BoardWindowItem | BoardWindowSpacer;

interface WindowedItem {
    index: number;
    start: number;
    end: number;
}

interface GetWindowRunsArgs {
    items: readonly WindowedItem[];
    count: number;
    /** The list's flex gap; spacers sit between gaps like any child. */
    gap: number;
    /** Where the first item starts and the last one ends, in scroller content. */
    listStart: number;
    listEnd: number;
}

/**
 * Mounted items in order, with a spacer standing in for each run of
 * unmounted ones, so the list keeps its full height in normal flow.
 */
export const getWindowRuns = ({
    items,
    count,
    gap,
    listStart,
    listEnd,
}: GetWindowRunsArgs): BoardWindowRun[] => {
    if (items.length === 0) {
        return count === 0
            ? []
            : [
                  {
                      kind: "spacer",
                      key: "spacer:all",
                      size: listEnd - listStart,
                  },
              ];
    }
    const runs: BoardWindowRun[] = [];
    let previous: WindowedItem | null = null;
    items.forEach((item) => {
        if (previous === null && item.index > 0) {
            runs.push({
                kind: "spacer",
                key: `spacer:${item.index}`,
                size: item.start - listStart - gap,
            });
        } else if (previous !== null && item.index > previous.index + 1) {
            runs.push({
                kind: "spacer",
                key: `spacer:${item.index}`,
                size: item.start - previous.end - 2 * gap,
            });
        }
        runs.push({ kind: "item", index: item.index });
        previous = item;
    });
    const last = items[items.length - 1];
    if (last.index < count - 1) {
        runs.push({
            kind: "spacer",
            key: "spacer:end",
            size: listEnd - last.end - gap,
        });
    }
    return runs;
};
